import React, { useState } from 'react';
import styled from 'styled-components';
import { Icon } from 'components/common';
import { colours } from 'utils/theme';
import CollapsiblePanel from '../CollapsiblePanel';

const Container = styled.div`
  font-size: 14px;
  border: 1px solid
    ${p => (p.$sectionHeader ? colours.borderGrey : p.theme.lightGrey)};
  border-radius: 4px;
  padding: ${p => (p.$sectionHeader ? '16px' : '10px')};
`;

const Title = styled.div`
  user-select: none;
  font-weight: 500;
  font-size: ${p => (p.$sectionHeader ? '14px' : 'inherit')};
  line-height: ${p => (p.$sectionHeader ? '22px' : 'inherit')};
  color: ${p => (p.$sectionHeader ? colours.character.primary85 : 'inherit')};
  cursor: pointer;
  padding: ${p =>
    p.$sectionHeader ? (p.$isCollapsed ? '0' : '0 0 12px 0') : '10px 0'};
  display: flex;
  align-items: center;
  gap: ${p => (p.$sectionHeader ? '8px' : 0)};
  margin-left: ${p => (p.$sectionHeader ? 0 : '10px')};
`;

const IconSlot = styled.div`
  width: 30px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Content = styled.div`
  padding: ${p => (p.$sectionHeader ? '0' : '0px 10px 10px')};
  padding-left: ${p => (p.$sectionHeader ? 0 : 10) + (p.$bodyIndent || 0)}px;
  > * {
    margin-bottom: 10px;
    :last-child {
      margin-bottom: 5px;
    }
  }
`;

const RotatingIconWrapper = styled.div`
  margin-left: auto;
  transform: ${p => (p.up ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 0.2s linear;
`;

/**
 * A reusable accordion component that can be collapsed/expanded
 *
 * @param {Object} props
 * @param {string|React.ReactNode} props.title - The title shown in the accordion header
 * @param {React.ReactNode} props.children - Content to show when expanded
 * @param {string} [props.icon] - Optional icon to show next to title
 * @param {boolean} [props.isCollapsedByDefault=false] - Initial collapsed state
 * @param {string} [props.testId='accordion'] - Test ID for the component
 * @param {string} [props.iconColor] - Color for the icon if provided
 * @param {boolean} [props.sectionHeader=false] - Align header with section titles (e.g. NLP modal); icon uses primary85 like AI feedback control
 * @param {number} [props.bodyIndent=0] - Extra left padding (px) on the body, so content lines up with the title text when the title carries a leading control in a fixed slot
 */
const Accordion = ({
  title,
  children,
  icon,
  isCollapsedByDefault = false,
  isCollapsed: isCollapsedProp,
  onToggleCollapsed,
  testId = 'accordion',
  iconColor,
  sectionHeader = false,
  bodyIndent = 0,
}) => {
  const isControlled = isCollapsedProp !== undefined;
  const [internalCollapsed, setInternalCollapsed] = useState(
    isCollapsedByDefault
  );
  const isCollapsed = isControlled ? isCollapsedProp : internalCollapsed;
  const handleToggle = () => {
    if (isControlled) {
      onToggleCollapsed?.(!isCollapsed);
    } else {
      setInternalCollapsed(prev => !prev);
    }
  };

  const resolvedIconColor =
    iconColor !== undefined
      ? iconColor
      : sectionHeader
      ? colours.character.primary85
      : colours.darkGrey;

  return (
    <Container data-testid={testId} $sectionHeader={sectionHeader}>
      <Title
        data-testid={`${testId}-title`}
        $sectionHeader={sectionHeader}
        $isCollapsed={isCollapsed}
        onClick={handleToggle}
      >
        {icon &&
          (sectionHeader ? (
            <Icon icon={icon} color={resolvedIconColor} size='sm' />
          ) : (
            <IconSlot>
              <Icon icon={icon} color={resolvedIconColor} size='sm' />
            </IconSlot>
          ))}
        {title}
        <RotatingIconWrapper up={!isCollapsed}>
          <Icon icon='chevronDown' size='sm' color={colours.darkGrey} />
        </RotatingIconWrapper>
      </Title>
      <CollapsiblePanel isCollapsed={isCollapsed}>
        <Content $sectionHeader={sectionHeader} $bodyIndent={bodyIndent}>
          {children}
        </Content>
      </CollapsiblePanel>
    </Container>
  );
};

export default Accordion;
